import { TreatmentFormFields } from 'components/cattles/treatmentPage/types/types'
import { morphForWords } from 'components/shared/processes'
import { ITreatmentSchema } from 'models/ICattlesData'

export const TREATMENT_SCHEMA_TITLE_MAX_LENGTH = 100
export const TREATMENT_SCHEMA_DESCRIPTION_MAX_LENGTH = 1000
export const TREATMENT_SCHEMA_TITLE_DISPLAY_LENGTH = 50
export const TREATMENT_SCHEMA_DESCRIPTION_DISPLAY_LENGTH = 140
export const TREATMENT_SCHEMA_DIAGNOSIS_MAX_LENGTH = 50

type SicknessLike = {
	id?: number
	title?: string
}

const toSicknessList = (value?: SicknessLike | SicknessLike[] | null) => {
	if (!value) return []
	return Array.isArray(value) ? value : [value]
}

export const truncateWithEllipsis = (
	value: string | null | undefined,
	maxLength: number,
) => {
	const text = (value ?? '').trim()
	if (!text) return '-'
	if (text.length <= maxLength) return text
	return `${text.slice(0, maxLength)}...`
}

export const toNumericId = (value: unknown) => {
	const id = Number(value)
	return Number.isInteger(id) && id > 0 ? id : 0
}

const toIdList = (
	value?: number | number[] | string | (number | string)[] | null,
) => {
	if (value === null || value === undefined || value === '') return []
	const list = Array.isArray(value) ? value : [value]
	return list.map(toNumericId).filter(Boolean)
}

export const getTreatmentSchemaDiagnoses = (
	schema: Pick<
		ITreatmentSchema,
		'sickness' | 'sicknesses' | 'sickness_id' | 'sickness_ids' | 'sicknesses_id'
	>,
) => {
	const unique = new Map<number | string, { id: number; title: string }>()

	const addDiagnose = (id: number, title = '') => {
		const numericId = toNumericId(id)
		if (!numericId && !title) return
		const key = numericId || title
		const current = unique.get(key)
		if (!current) {
			unique.set(key, { id: numericId, title })
			return
		}
		if (!current.title && title)
			unique.set(key, { id: current.id || numericId, title })
	}

	toSicknessList(schema.sickness).forEach(item => {
		if (!item) return
		addDiagnose(item.id ?? 0, item.title?.trim() || '')
	})
	toSicknessList(schema.sicknesses).forEach(item => {
		if (!item) return
		addDiagnose(item.id ?? 0, item.title?.trim() || '')
	})
	;[
		...toIdList(schema.sickness_ids),
		...toIdList(schema.sickness_id),
		...toIdList(schema.sicknesses_id),
	].forEach(id => addDiagnose(id))

	return Array.from(unique.values())
}

export const resolveDiagnoseTitles = (
	diagnoses: { id: number; title: string }[],
	diagnosesList: { id: number; title: string }[] = [],
) =>
	diagnoses
		.map(diagnose => ({
			...diagnose,
			title:
				diagnose.title ||
				diagnosesList.find(item => item.id === diagnose.id)?.title ||
				'',
		}))
		.filter(diagnose => diagnose.title || diagnose.id > 0)

export const getExaminationInterval = (
	schema: Pick<
		ITreatmentSchema,
		'exam_interval_days' | 'examination_interval' | 'inspection_interval'
	>,
) =>
	schema.exam_interval_days ??
	schema.examination_interval ??
	schema.inspection_interval

export const formatTreatmentDuration = (
	duration: number | string | null | undefined,
) => {
	if (duration === null || duration === undefined || duration === '') return '-'
	const value = Number(duration)
	if (!Number.isInteger(value) || value < 0) return '-'
	return `${value} ${morphForWords(value, ['день', 'дня', 'дней'])}`
}

export const getTreatmentSchemaName = (schema: ITreatmentSchema) =>
	schema.title?.trim() || schema.description?.trim() || ''

export const getDeleteTreatmentSchemaMessage = (
	schemaName: string,
	diagnosisNames: string[],
) => {
	const name = schemaName.trim()
	const diagnoses = diagnosisNames.map(item => item.trim()).filter(Boolean)
	const schemaPart = name ? `Схема лечения "${name}"` : 'Схема лечения'

	if (!diagnoses.length) {
		return `${schemaPart}\nбудет удалена.`
	}

	const diagnosisLabel =
		diagnoses.length === 1 ? 'при диагнозе' : 'при диагнозах'
	const diagnosisPart = diagnoses.map(item => `"${item}"`).join(', ')

	return `${schemaPart} ${diagnosisLabel} ${diagnosisPart}\nбудет удалена.`
}

export const formatExaminationFrequency = (
	interval: number | string | null | undefined,
) => {
	if (interval === null || interval === undefined || interval === '') return '-'
	const value = Number(interval)
	if (!Number.isInteger(value) || value < 1) return '-'
	return `Проводить осмотр каждый ${value} день в период лечения.`
}

export const DAY_IN_MS = 24 * 60 * 60 * 1000

export const startOfDay = (timestamp: number) => {
	const date = new Date(timestamp)
	date.setHours(0, 0, 0, 0)
	return date.getTime()
}

export const getTreatmentExaminationDates = (
	startOfDayTs: number,
	durationDays: number | string | null | undefined,
	intervalDays: number | string | null | undefined,
) => {
	const duration = Number(durationDays)
	if (!Number.isInteger(duration) || duration < 1) return [] as number[]

	const interval = Number(intervalDays)
	const step = Number.isInteger(interval) && interval > 0 ? interval : 1
	const dates: number[] = []

	for (let day = 0; day <= duration; day += step) {
		dates.push(startOfDayTs + day * DAY_IN_MS)
	}

	return dates
}

export const formatExaminationPeriodicityShort = (
	interval: number | string | null | undefined,
) => {
	if (interval === null || interval === undefined || interval === '') return '-'
	const value = Number(interval)
	if (!Number.isInteger(value) || value < 1) return '-'
	if (value === 1) return 'ежедневно'
	if (value === 2) return 'через день'
	return `каждые ${value} ${morphForWords(value, ['день', 'дня', 'дней'])}`
}

export const getMilkRejectPeriod = (
	startOfDayTs: number,
	milkRejectDays: number | string | null | undefined,
) => {
	const days = Number(milkRejectDays)
	if (!Number.isInteger(days) || days < 1) return null

	return {
		start: startOfDayTs,
		end: startOfDayTs + days * DAY_IN_MS,
		days,
	}
}

export const getMaxTreatmentDuration = (schemas: ITreatmentSchema[] = []) => {
	const days = schemas
		.map(schema => Number(schema.duration))
		.filter(value => Number.isInteger(value) && value >= 0)
	if (!days.length) return null
	return Math.max(...days)
}

export const getCombinedMilkRejectPeriod = (
	schemas: ITreatmentSchema[] = [],
	now = Date.now(),
) => {
	const today = startOfDay(now)
	const periods = schemas
		.map(schema => getMilkRejectPeriod(today, schema.milk_reject_days))
		.filter(
			(period): period is NonNullable<ReturnType<typeof getMilkRejectPeriod>> =>
				period !== null,
		)

	if (!periods.length) return null

	const start = Math.min(...periods.map(period => period.start))
	const end = Math.max(...periods.map(period => period.end))
	return {
		start,
		end,
		days: Math.round((end - start) / DAY_IN_MS),
	}
}

export const toTreatmentSchemaOptionsByDiagnoses = (
	schemas: ITreatmentSchema[] = [],
	selectedDiagnosisIds: number[] = [],
	diagnosesList: { id: number; title: string }[] = [],
) => {
	const selectedIds = selectedDiagnosisIds.map(toNumericId).filter(Boolean)

	return schemas
		.map(schema => {
			const diagnoses = resolveDiagnoseTitles(
				getTreatmentSchemaDiagnoses(schema),
				diagnosesList,
			)
			const matching = selectedIds.length
				? diagnoses.filter(diagnose =>
						selectedIds.includes(toNumericId(diagnose.id)),
					)
				: diagnoses

			if (selectedIds.length > 0 && matching.length === 0) {
				return null
			}

			const groupTitles = (matching.length ? matching : diagnoses)
				.map(diagnose => diagnose.title)
				.filter(Boolean)

			return {
				id: schema.id,
				title: getTreatmentSchemaName(schema) || `Схема лечения ${schema.id}`,
				group: groupTitles.join(', ') || 'Без диагноза',
			}
		})
		.filter(
			(option): option is { id: number; title: string; group: string } =>
				option !== null,
		)
		.sort(
			(a, b) =>
				a.group.localeCompare(b.group, 'ru') ||
				a.title.localeCompare(b.title, 'ru'),
		)
}

export const schemaMatchesDiagnoses = (
	schema: ITreatmentSchema,
	diagnosisIds: number[],
) => {
	const selectedIds = diagnosisIds.map(toNumericId).filter(Boolean)
	if (!selectedIds.length) return false
	return getTreatmentSchemaDiagnoses(schema).some(diagnose =>
		selectedIds.includes(toNumericId(diagnose.id)),
	)
}

type TreatmentSchemaOption = {
	id: number
	title: string
	group: string
}

export const pickTreatmentSchemaOptionsForDiagnoses = (
	options: TreatmentSchemaOption[] = [],
	schemas: ITreatmentSchema[] = [],
	selectedDiagnosisIds: number[] = [],
	diagnosesList: { id: number; title: string }[] = [],
) => {
	if (!options.length || !selectedDiagnosisIds.length) return []

	const selectedIds = selectedDiagnosisIds.map(toNumericId).filter(Boolean)
	const matchingIds = new Set(
		schemas
			.filter(schema => schemaMatchesDiagnoses(schema, selectedIds))
			.map(schema => toNumericId(schema.id)),
	)

	if (matchingIds.size) {
		const filtered = options.filter(option =>
			matchingIds.has(toNumericId(option.id)),
		)
		if (filtered.length) return filtered
	}

	const selectedTitles = diagnosesList
		.filter(item => selectedIds.includes(toNumericId(item.id)))
		.map(item => item.title?.trim())
		.filter(Boolean)

	if (!selectedTitles.length) return options

	const byGroup = options.filter(option => {
		const groups = (option.group || '')
			.split(',')
			.map(title => title.trim())
			.filter(Boolean)
		return selectedTitles.some(title => groups.includes(title))
	})

	return byGroup.length ? byGroup : options
}

export const getCheckupScheduleFromDiagnosis = (
	treatmentPlanIds: number[],
	schemas: ITreatmentSchema[] = [],
	now = Date.now(),
) => {
	const startOfToday = startOfDay(now)
	const selected = schemas.filter(schema =>
		treatmentPlanIds.includes(schema.id),
	)
	const examDates = selected
		.flatMap(schema =>
			getTreatmentExaminationDates(
				startOfToday,
				schema.duration,
				getExaminationInterval(schema),
			),
		)
		.sort((a, b) => a - b)

	const nextExaminationDate =
		examDates.find(date => date > startOfToday) ?? examDates[0] ?? null

	const maxRejectDays = selected.reduce((max, schema) => {
		const days = Number(schema.milk_reject_days)
		if (!Number.isInteger(days) || days < 1) return max
		return Math.max(max, days)
	}, 0)
	const milkReject = getMilkRejectPeriod(startOfToday, maxRejectDays || null)

	return {
		nextExaminationDate,
		milkRejectStart: milkReject?.start ?? null,
		milkRejectEnd: milkReject?.end ?? null,
	}
}

export const getSicknessIds = (ids: number[]) =>
	ids.filter(id => Number.isInteger(id) && id > 0)

export const getSicknessPayload = (ids: number[]) => getSicknessIds(ids)[0]

export const toCreateTreatmentPayload = (field: TreatmentFormFields) => {
	const sicknessIds = getSicknessIds(field.selectedDiagnose)
	return {
		title: field.title.trim(),
		description: field.description.trim(),
		duration: Number(field.duration),
		sickness_id: sicknessIds[0],
		sicknesses_id: sicknessIds,
		exam_interval_days: Number(field.examinationInterval),
		milk_reject_days:
			field.rejectMilk && field.milkRejectDays
				? Number(field.milkRejectDays)
				: null,
	}
}

export const toUpdateTreatmentPayload = (field: TreatmentFormFields) => ({
	...toCreateTreatmentPayload(field),
	duration: Number(field.duration),
})
